import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LogoutRounded } from '@mui/icons-material';

export const SignupShell = ({
  activeTab = 'signup',
  onNavigateLogin,
  onNavigateSignUp,
  // Set once the user has signed in (the page listing their apps): the Sign Up / Log In tabs
  // give way to who is signed in and a Log out button.
  signedInAs,
  onLogout,
  children,
}) => {
  const isSignedIn = signedInAs !== undefined && signedInAs !== null;
  const isSignupActive = activeTab === 'signup';
  const isLoginActive = activeTab === 'login';

  return (
    <Box
      data-testid="auth-shell"
      sx={{
        minHeight: '100vh',
        background: '#fefefe',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Box
        sx={{
          maxWidth: isSignedIn ? 1120 : 920,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 4 },
        }}
      >
        <Stack spacing={1} alignItems="center">
          <Box
            component="img"
            src="/assets/images/brandLogo/eK_Link_Logo.svg"
            alt="eK Link"
            sx={{ width: { xs: 52, md: 58 }, height: 'auto' }}
          />
          <Typography
            variant="body2"
            sx={{
              color: '#294190',
              fontWeight: 700,
              letterSpacing: 0.2,
              mt: -0.5,
            }}
          >
            eK Link - Endless Potential
          </Typography>
        </Stack>

        {isSignedIn ? (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            data-testid="auth-signed-in"
            sx={{
              pl: 2,
              pr: 0.5,
              py: 0.5,
              borderRadius: 2,
              backgroundColor: '#ffffff',
              border: '1px solid',
              borderColor: 'rgba(79, 133, 6, 0.22)',
              boxShadow: '0 8px 24px rgba(28, 27, 31, 0.06)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Signed in{signedInAs ? ' as ' : ''}
              {signedInAs ? (
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  {signedInAs}
                </Box>
              ) : null}
            </Typography>
            <Button
              variant="text"
              onClick={onLogout}
              startIcon={<LogoutRounded />}
              data-testid="auth-logout"
              sx={{
                minHeight: 42,
                borderRadius: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': { backgroundColor: 'rgba(79, 133, 6, 0.10)' },
              }}
            >
              Log out
            </Button>
          </Stack>
        ) : (
        <Stack
          direction="row"
          spacing={0}
          sx={{
            p: 0.5,
            borderRadius: 2,
            backgroundColor: '#ffffff',
            border: '1px solid',
            borderColor: 'rgba(79, 133, 6, 0.22)',
            boxShadow: '0 8px 24px rgba(28, 27, 31, 0.06)',
          }}
        >
          <Button
            variant={isSignupActive ? 'contained' : 'text'}
            disableElevation
            onClick={onNavigateSignUp}
            data-testid="nav-signup"
            aria-current={isSignupActive ? 'page' : undefined}
            sx={{
              minWidth: { xs: 96, sm: 112 },
              minHeight: 42,
              borderRadius: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: isSignupActive ? 'primary.main' : 'transparent',
              color: isSignupActive ? '#ffffff' : 'text.primary',
              '&:hover': {
                backgroundColor: isSignupActive
                  ? '#4d7d0f'
                  : 'rgba(79, 133, 6, 0.10)',
                color: isSignupActive ? '#ffffff' : 'text.primary',
              },
            }}
          >
            Sign Up
          </Button>
          <Button
            variant={isLoginActive ? 'contained' : 'text'}
            onClick={onNavigateLogin}
            data-testid="nav-login"
            aria-current={isLoginActive ? 'page' : undefined}
            sx={{
              minWidth: { xs: 96, sm: 112 },
              minHeight: 42,
              borderRadius: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: isLoginActive ? 'primary.main' : 'transparent',
              color: isLoginActive ? '#ffffff' : 'text.primary',
              '&:hover': {
                backgroundColor: isLoginActive
                  ? '#4d7d0f'
                  : 'rgba(79, 133, 6, 0.10)',
                color: isLoginActive ? '#ffffff' : 'text.primary',
              },
            }}
          >
            Log In
          </Button>
        </Stack>
        )}

        <Box sx={{ width: '100%' }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default SignupShell;
